/**
 * The AI-assistant chat panel and message rendering. A view module: reads chat
 * signals and emits data-action attributes. The role-aware shared shell uses it
 * for both staff and requester demand conversations.
 */
import './chat.css';

import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { ListItem } from '@kerfjs/ui/list-item';
import { Text } from '@kerfjs/ui/text';
import type { WorkbenchPanel } from '@kerfjs/ui/workbench';

import { ProposedFactsCard } from '../components/proposed-facts-card.js';
import { chatActiveId, chatConversations, chatHistoryOpen, chatTarget, workspace } from '../data/state.js';
import { assistantToolbar } from './chat/components/assistant-toolbar.js';
import { ChatComposer } from './chat/components/chat-composer.js';
import { ChatTranscript } from './chat/components/chat-transcript.js';

export { ChatMessageView } from './chat/components/chat-message-view.js';

/** The demand AI-assistant rail (HS2-ZXJJSS) as a Workbench panel: retained chat
 *  with tool activity, citations, confirmation cards, and a History switcher
 *  across conversations. Kerf composes its toolbar and relocates the toggle while
 *  the rail is collapsed (HS2-AC9K4G). The transcript is the rail pane's scrolling
 *  content, and the composer floats at its bottom (HS2-RYZJCT). */
export function assistantPanel({ view: v, collapsed }: { view: string; collapsed: boolean }): WorkbenchPanel {
  const conversations = chatConversations.value;
  const active = hasAiPanel(v); // a conversation is in scope (a demand workspace or the library)
  const isDemand = chatTarget.value?.scope === 'demand';
  if (v === 'conversations' || v === 'savings' || v === 'settings') {
    // Chat, Stats and Settings share the minimal Inspector (HS2-JWV3EZ):
    // only the collapse control and a placeholder, without an assistant title.
    return {
      label: 'Inspector',
      collapsed,
      toolbar: assistantToolbar({ label: 'Inspector', title: null }),
      content: (
        <List className="chat-scroll-body project-chat-placeholder" gap="xs" controlInsets="trbl">
          <Text tone="quiet" size="compact">
            Nothing to inspect here yet.
          </Text>
        </List>
      ),
    };
  }
  if (!active) {
    // Always present, but nothing to talk to yet — show a placeholder (HS2-G7SRW3).
    return {
      label: 'AI Assistant',
      collapsed,
      toolbar: assistantToolbar(),
      content: (
        <List className="chat-scroll-body" gap="xs" controlInsets="trbl">
          <Text tone="quiet" size="compact">
            Open a Demand to chat with the AI assistant. It answers with cited sources and confirms before changing
            anything.
          </Text>
        </List>
      ),
    };
  }
  return {
    label: 'AI Assistant',
    collapsed,
    // A demand has a single conversation; History/New only apply to the library
    // scope, where separate threads make sense.
    toolbar: assistantToolbar({
      scopeActions: isDemand ? undefined : (
        <>
          {conversations.length > 0 ? (
            <Button tone="neutral" emphasis="plain" size="compact" action="chat-history">
              {`History (${conversations.length})`}
            </Button>
          ) : null}
          <Button tone="neutral" emphasis="plain" size="compact" action="chat-new" title="Start a new conversation">
            ＋ New
          </Button>
        </>
      ),
    }),
    // The composer floats over the transcript instead of sitting in a solid footer
    // strip: a sticky wrapper at the bottom of the scrolling content, like the center
    // project chat, so messages scroll beneath it (HS2-RYZJCT).
    content: (
      <div class="assistant-chat">
        <List className="chat-scroll-body" gap="xs" controlInsets="trbl" flex>
          {!isDemand && chatHistoryOpen.value ? (
            <List className="chat-history" gap="2xs">
              {conversations.map((c) => (
                <ListItem
                  label={c.title ?? 'Conversation'}
                  description={`${String(c.messageCount)} msg`}
                  action="chat-select"
                  itemId={c.id}
                  selected={c.id === chatActiveId.value}
                />
              ))}
            </List>
          ) : null}
          {/* Proposed-facts review lives in the docked panel on the library view
            (HS2-DSMCRT), not the main content column. */}
          {v === 'library' ? <ProposedFactsCard /> : null}
          <ChatTranscript
            emptyText={
              chatTarget.value?.scope === 'demand'
                ? 'Ask about this demand — a fair price, a benchmark, a spec. I cite my sources, and I confirm before changing anything.'
                : 'Ask about the background library — what we know, or to reconcile two documents. I cite my sources and propose facts for your review.'
            }
          />
        </List>
        <div class="assistant-chat-composer">
          <ListInsetControl>
            <ChatComposer />
          </ListInsetControl>
        </div>
      </div>
    ),
  };
}

/** Shared message stream + composer used by the center project-chat screen. */
export { ChatConversation } from './chat/components/chat-conversation.js';
export { ProjectChatView } from './chat/components/project-chat-view.js';

/** True where the AI assistant docks beside the content. */
function hasAiPanel(v: string): boolean {
  return (v === 'workspace' && workspace.value !== null) || v === 'library';
}
